import { useEffect, useState, useContext } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import "./MenuNotifications.scss";
import { AuthContext } from "../../../context/auth-context";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import CloseIcon from "@mui/icons-material/Close"; // Added close icon

import {
  Eye,
  Syringe,
  Stethoscope,
  CalendarClock,
  Info,
  FileText,
  FileCheck,
  CalendarOff,
  CalendarCheck,
  Phone,
  History,
  Building2,
  AlertTriangle,
  Printer,
  Siren,
} from "lucide-react";

function MenuNotifications({
  anchorEl,
  handleCloseNotification,
  openNotification,
  notificacoes,
  handleDispensarNotificacao,
  handleClickNotificacao,
  handleApagarTodasNotificacoes,
  onVerTudo,
}) {
  const [notifications, setNotifications] = useState();
  const auth = useContext(AuthContext);
  const needsDoctorInfo = ["Assistente", "Secretária(o)"].includes(auth.role);

  useEffect(() => {
    setNotifications(notificacoes);
  }, [notificacoes]);

  // Group notifications by doctor if needed
  const getGroupedNotifications = () => {
    if (!needsDoctorInfo || !notifications)
      return { ungrouped: notifications || [] };

    const grouped = {};

    notifications.forEach((notification) => {
      const medicoId = notification.medicoId || "ungrouped";
      if (!grouped[medicoId]) {
        grouped[medicoId] = [];
      }
      grouped[medicoId].push(notification);
    });

    return grouped;
  };
  // Adicione esta função antes do return, após o formatDateTime
  const getNotificationIcon = (tipo) => {
    const iconStyle = { fontSize: "20px", marginRight: "12px", flexShrink: 0 };

    switch (tipo) {
      case "espera avaliação":
        return <FileText style={{ ...iconStyle, color: "rgb(245, 124, 0)" }} />;
      case "espera observação":
        return <Eye style={{ ...iconStyle, color: "#9c27b0" }} />;
      case "espera consulta":
        return <Stethoscope style={{ ...iconStyle, color: "#2e7d32" }} />;
      case "criou tratamento isolado":
        return <Syringe style={{ ...iconStyle, color: "rgb(93, 64, 55)" }} />;
      case "efetuou avaliação":
        return <FileCheck style={{ ...iconStyle, color: "#0288d1" }} />;
      case "reagendamento":
        return <CalendarClock style={{ ...iconStyle, color: "#d32f2f" }} />;
      case "agendamentosOrfaos":
        return <CalendarOff style={{ ...iconStyle, color: "#e74c3c" }} />;
      case "limpezaRealizada":
        return <CalendarCheck style={{ ...iconStyle, color: "#00897b" }} />;
      case "agendamentoProvisorioCriado":
        return <CalendarCheck style={{ ...iconStyle, color: "#00897b" }} />;
      case "lembreteAgendamentoProvisorio":
        return <Phone style={{ ...iconStyle, color: "#ff6f00" }} />;
      case "EMPRESA_PARCEIRA_EXPIRADA":
        return <Building2 style={{ ...iconStyle, color: "#f44336" }} />;
      case "EMPRESA_PARCEIRA_A_EXPIRAR":
        return <AlertTriangle style={{ ...iconStyle, color: "#ff9800" }} />;
      /* Os cinco tipos de documento usam a MESMA impressora: o que o aviso pede
         é sempre o mesmo — há papel para sair. Qual é o documento está escrito
         na mensagem, e o clique leva ao separador certo.

         O índigo é o único tom livre: laranja, púrpura, verde, castanho,
         azul-claro, vermelho, turquesa e âmbar já estão tomados pelos avisos
         acima, e o cinzento é o do `Info` por omissão. */
      case "documentoParaImprimir":
        return <Printer style={{ ...iconStyle, color: "#3949ab" }} />;
      /* Um paciente esta na sala de espera com dor. E o unico aviso que tem
         direito a ser o mais visivel da lista: a sirene e o vermelho forte nao
         sao usados por mais nenhum. */
      case "urgencia":
        return <Siren style={{ ...iconStyle, color: "#b91c1c" }} />;
      case "urgencia-escalada":
        return (
          <Siren
            style={{ ...iconStyle, color: "#b91c1c" }}
            strokeWidth={2.75}
          />
        );
      default:
        return <Info style={{ ...iconStyle, color: "#757575" }} />;
    }
  };

  const groupedNotifications = getGroupedNotifications();
  const doctorGroups = Object.keys(groupedNotifications);
  const showGroups = needsDoctorInfo && doctorGroups.length > 1;

  // Find doctor name from usuariosAssociados
  const getDoctorName = (medicoId) => {
    if (!auth.usuariosAssociados || !medicoId || medicoId === "ungrouped")
      return "Outros";

    const doctor = auth.usuariosAssociados.find((m) => m.id === medicoId);
    return doctor ? doctor.nome : "Médico";
  };

  // Format date and time
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";

    try {
      const date = new Date(dateTimeString);

      // Check if date is valid
      if (isNaN(date.getTime())) return "";

      // Format: DD/MM/YYYY HH:MM
      return `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")} ${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    } catch (error) {
      return "";
    }
  };

  return (
    <>
      {notifications && (
        <>
          <Menu
            id="menu-notifications"
            anchorEl={anchorEl}
            open={openNotification}
            onClose={handleCloseNotification}
            className="menuNotificacao"
            PaperProps={{
              style: {
                maxWidth: "500px", // Wider menu
                maxHeight: "600px", // Taller menu for more notifications
              },
            }}
            MenuListProps={{
              style: {
                padding: 0,
              },
            }}
          >
            <div className="menuNotificacao__cabecalho">
              <span className="menuNotificacao__cabecalho__titulo">
                Notificações
              </span>

              <div
                className="menuNotificacao__cabecalho__verTudo__container"
                onClick={onVerTudo}
              >
                <History className="menuNotificacao__cabecalho__verTudo__icon" />
                <span className="menuNotificacao__cabecalho__verTudo">
                  Ver tudo
                </span>
              </div>

              {/* ⭐ Só mostrar "Limpar tudo" se houver notificações */}
              {notificacoes.length > 0 && (
                <div className="menuNotificacao__cabecalho__limparTudo__container">
                  <MarkEmailReadIcon className="menuNotificacao__cabecalho__limparTudo__icon" />
                  <span
                    className="menuNotificacao__cabecalho__limparTudo"
                    onClick={handleApagarTodasNotificacoes.bind(
                      null,
                      auth.userId
                    )}
                  >
                    Limpar tudo
                  </span>
                </div>
              )}
            </div>

            {notificacoes.length === 0 ? (
              <MenuItem style={{ fontSize: "15px", padding: "20px" }}>
                Não há notificações
              </MenuItem>
            ) : showGroups ? (
              // Grouped notifications
              doctorGroups.map((medicoId) => (
                <div key={medicoId} className="menuNotificacao__grupo">
                  {medicoId !== "ungrouped" && (
                    <div className="menuNotificacao__grupo__titulo">
                      {getDoctorName(medicoId)}
                    </div>
                  )}

                  {groupedNotifications[medicoId].map((n) => (
                    <MenuItem
                      className="MenuItemNotifications"
                      key={crypto.randomUUID()}
                      disableGutters={true} /* Remove default padding */
                    >
                      <div className="MenuItemNotifications__content">
                        {getNotificationIcon(n.tipo)}
                        <span
                          className="MenuItemNotifications__message"
                          onClick={handleClickNotificacao.bind(
                            null,
                            n.id,
                            n.tipo,
                            n.cliente?.id,
                            n.dados,
                            //não é uma notificação permanente
                            false
                          )}
                        >
                          {/* Ensure message text wraps for long content */}
                          {n.mensagem.split("\n").map((text, i) => (
                            <span
                              key={i}
                              style={{
                                display: "block",
                                marginBottom:
                                  i < n.mensagem.split("\n").length - 1
                                    ? "4px"
                                    : 0,
                              }}
                            >
                              {text}
                            </span>
                          ))}
                          {n.dados?.observacao && (
                            <span className="MenuItemNotifications__obs">
                              📝 {n.dados.observacao}
                            </span>
                          )}
                          {/* Mostrar data/hora da notificação */}
                          {n.created_at && (
                            <span className="MenuItemNotifications__datetime">
                              {formatDateTime(n.created_at)}
                            </span>
                          )}
                        </span>
                      </div>
                      <span
                        className="MenuItemNotifications__closeIcon closeNotification"
                        onClick={handleDispensarNotificacao.bind(null, n.id)}
                        title="Dispensar notificação"
                      >
                        <CloseIcon fontSize="small" />
                      </span>
                    </MenuItem>
                  ))}
                </div>
              ))
            ) : (
              // Non-grouped notifications
              notifications.map((n) => (
                <MenuItem
                  className="MenuItemNotifications"
                  key={crypto.randomUUID()}
                  disableGutters={true} /* Remove default padding */
                >
                  <div className="MenuItemNotifications__content">
                    {getNotificationIcon(n.tipo)}
                    <span
                      className="MenuItemNotifications__message"
                      onClick={handleClickNotificacao.bind(
                        null,
                        n.id,
                        n.tipo,
                        n.cliente?.id,
                        n.dados,
                        //não é uma notificação permanente
                        false
                      )}
                    >
                      {needsDoctorInfo && n.medicoId && (
                        <span className="MenuItemNotifications__doctor">
                          {getDoctorName(n.medicoId)}:
                        </span>
                      )}
                      {/* Handle line breaks for long messages */}
                      {n.mensagem.split("\n").map((text, i) => (
                        <span
                          className="MenuItemNotifications__messageText"
                          key={i}
                          style={{
                            display: "block",
                            marginBottom:
                              i < n.mensagem.split("\n").length - 1 ? "4px" : 0,
                          }}
                        >
                          {text}
                        </span>
                      ))}
                      {n.dados?.observacao && (
                        <span className="MenuItemNotifications__obs">
                          📝 {n.dados.observacao}
                        </span>
                      )}
                      {/* Mostrar data/hora da notificação */}
                      {n.created_at && (
                        <span className="MenuItemNotifications__datetime">
                          {formatDateTime(n.created_at)}
                        </span>
                      )}
                    </span>
                  </div>
                  <span
                    className="MenuItemNotifications__closeIcon closeNotification"
                    onClick={handleDispensarNotificacao.bind(null, n.id)}
                    title="Dispensar notificação"
                  >
                    <CloseIcon fontSize="small" />
                  </span>
                </MenuItem>
              ))
            )}
          </Menu>
        </>
      )}
    </>
  );
}

export default MenuNotifications;
